import React, { useState } from 'react';
import { useStudioStore } from '../../stores/useStudioStore';
import { studioApiService } from '../../services/studioApiService';

export const VarPanelB: React.FC = () => {
  const { 
    openaiKey,
    variantAngles, setVariantAngles,
    variantDefs, setVariantDefs,
    variantQueue, setVariantQueue
  } = useStudioStore();

  const [isGenerating, setIsGenerating] = useState(false);

  const addAngle = () => {
    setVariantAngles([...variantAngles, { id: crypto.randomUUID(), label: `Angle ${variantAngles.length + 1}` }]);
  };

  const addVariantDef = () => {
    setVariantDefs([...variantDefs, { id: crypto.randomUUID(), label: `Variant ${variantDefs.length + 1}`, productId: '', notes: '' }]);
  };

  const handleAngleFile = (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    const previewUrl = URL.createObjectURL(file);
    setVariantAngles(variantAngles.map(a => a.id === id ? { ...a, file, previewUrl } : a));
  };

  const handleDefFile = (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    const previewUrl = URL.createObjectURL(file);
    setVariantDefs(variantDefs.map(d => d.id === id ? { ...d, file, previewUrl } : d));
  };

  const handleGenerateVariants = async () => {
    if (!openaiKey) {
      alert("Please enter OpenAI API key");
      return;
    }
    
    const validAngles = variantAngles.filter(a => a.file);
    const validDefs = variantDefs.filter(d => d.file);
    
    if (validAngles.length === 0 || validDefs.length === 0) {
      alert("Ensure you have at least one reference angle shot and one variant swatch.");
      return;
    }

    setIsGenerating(true);
    const newQueue = [...variantQueue];
    
    for (const def of validDefs) {
      for (const angle of validAngles) {
        const id = crypto.randomUUID();
        newQueue.push({
          id,
          angleLabel: angle.label,
          variantLabel: def.productId || def.label,
          status: 'generating',
          reviewState: 'pending'
        });
        setVariantQueue([...newQueue]);

        try {
          const res = await studioApiService.generateImages(openaiKey, 'fashion variant', 'top', 'high', '1024x1024', [
            { file: angle.file!, previewUrl: angle.previewUrl! },
            { file: def.file!, previewUrl: def.previewUrl! }
          ]);
          
          const b64 = res?.data?.[0]?.b64_json;
          if (b64) {
            setVariantQueue(q => q.map(p => p.id === id ? { ...p, status: 'complete', resultSrc: b64 } : p));
          } else {
            setVariantQueue(q => q.map(p => p.id === id ? { ...p, status: 'error', error: 'No image returned' } : p));
          }
        } catch (e: any) {
          setVariantQueue(q => q.map(p => p.id === id ? { ...p, status: 'error', error: e.message } : p));
        }
      }
    }
    
    setIsGenerating(false);
  };

  return (
    <div className="panel" id="varPanelB">
      <div className="panel-scroll">
        {/* Reference angles */}
        <div className="section">
          <div className="section-label">Reference angles (one finished shot per angle)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {variantAngles.map((angle, i) => (
              <div key={angle.id} className="product-card">
                <div className="product-card-header">
                  <div className="product-card-title">Angle {i + 1}</div>
                  <button className="product-card-remove" onClick={() => setVariantAngles(variantAngles.filter(a => a.id !== angle.id))}>×</button>
                </div>
                <div className="product-card-body" style={{ display: 'flex', flexDirection: 'column', padding: '12px 14px' }}>
                  <div className="card-drop-zone" style={{ minHeight: '120px', width: '100%' }}>
                    {angle.previewUrl ? (
                      <div className="card-thumbs">
                        <div className="card-thumb-wrap" style={{ width: '80px', height: '80px' }}>
                          <img src={angle.previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="card-drop-zone-icon">+</div>
                        <div className="card-drop-zone-label">Finished reference shot</div>
                        <div className="card-drop-zone-hint">(Drag and drop)</div>
                      </>
                    )}
                    <input type="file" accept="image/png, image/jpeg, image/webp" onChange={e => handleAngleFile(angle.id, e)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button className="add-product-btn" style={{ marginTop: '12px' }} onClick={addAngle}>+ Add angle</button>
        </div>

        {/* Variants */}
        <div className="section">
          <div className="section-label">Variants (color / print)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {variantDefs.map((def, i) => (
              <div key={def.id} className="product-card">
                <div className="product-card-header">
                  <div className="product-card-title">Variant {i + 1}</div>
                  <button className="product-card-remove" onClick={() => setVariantDefs(variantDefs.filter(d => d.id !== def.id))}>×</button>
                </div>
                <div className="product-card-body" style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '12px 14px' }}>
                  <div className="card-drop-zone" style={{ minHeight: '120px', width: '100%' }}>
                    {def.previewUrl ? (
                      <div className="card-thumbs">
                        <div className="card-thumb-wrap" style={{ width: '80px', height: '80px' }}>
                          <img src={def.previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="card-drop-zone-icon">+</div>
                        <div className="card-drop-zone-label">Color / print swatch</div>
                        <div className="card-drop-zone-hint">(Drag and drop)</div>
                      </>
                    )}
                    <input type="file" accept="image/png, image/jpeg, image/webp" onChange={e => handleDefFile(def.id, e)} />
                  </div>

                  <div className="pid-wrap" style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <input 
                      type="text"
                      className="pid-input"
                      placeholder="Product ID (e.g. 4088)"
                      value={def.productId}
                      onChange={e => setVariantDefs(variantDefs.map(d => d.id === def.id ? { ...d, productId: e.target.value } : d))}
                    />
                    <textarea 
                      className="pid-input" 
                      style={{ resize: 'vertical', minHeight: '36px', lineHeight: '1.4', width: '100%' }}
                      placeholder="Optional notes — e.g. motif scale"
                      value={def.notes || ''} 
                      onChange={e => setVariantDefs(variantDefs.map(d => d.id === def.id ? { ...d, notes: e.target.value } : d))} 
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button className="add-product-btn" style={{ marginTop: '12px' }} onClick={addVariantDef}>+ Add variant</button>
        </div>
      </div>

      <div className="run-controls">
        <button 
          className="run-btn primary" 
          onClick={handleGenerateVariants}
          disabled={isGenerating || variantAngles.length === 0 || variantDefs.length === 0}
        >
          {isGenerating ? 'Generating...' : '▶ Generate all variants'}
        </button>
      </div>
    </div>
  );
};
